import { useAtomValue, useSetAtom } from 'jotai'
import { mapSessionAtom } from '../../state/mapAtoms.ts'
import type { Tag } from '@/js/types'
import { useApi } from './useApi.ts'

export const useTags = () => {
    const mapSession = useAtomValue(mapSessionAtom)
    const setMapSession = useSetAtom(mapSessionAtom)
    const { callEndpoint } = useApi()

    const mapId = mapSession.id
    const endpoint = mapId ? `/api/maps/${mapId}/tags` : null

    const create = async (tag: { name: string; description?: string }) => {
        if (!endpoint) {
            throw new Error('Cannot create a tag without a map')
        }

        const response = await callEndpoint(endpoint, {
            method: 'POST',
            body: JSON.stringify({
                ...tag,
                name: tag.name.trim(),
            }),
        })

        if (!response.ok) {
            throw new Error(`Unable to create tag (${response.status})`)
        }

        const createdTag = (await response.json()) as Tag

        setMapSession((session) => {
            if (!session.map) {
                return session
            }

            return {
                ...session,
                map: {
                    ...session.map,
                    tags: [...(session.map.tags ?? []), createdTag],
                },
            }
        })

        return createdTag
    }

    const destroy = async (tag: Tag) => {
        if (!endpoint) {
            throw new Error('Cannot delete a tag without a map')
        }

        const response = await callEndpoint(`${endpoint}/${tag.id}`, {
            method: 'DELETE',
        })

        if (!response.ok) {
            throw new Error(`Unable to delete tag (${response.status})`)
        }

        setMapSession((session) => {
            if (!session.map) {
                return session
            }

            return {
                ...session,
                map: {
                    ...session.map,
                    tags: (session.map.tags ?? []).filter((currentTag) => String(currentTag.id) !== String(tag.id)),
                },
            }
        })

        return response.status
    }

    return {
        create,
        destroy,
    }
}
